export type Language = 'pl' | 'en';

export type TranslationKey =
  | 'layout.theme'
  | 'layout.themeLight'
  | 'layout.themeDark'
  | 'layout.language'
  | 'layout.languageEnglish'
  | 'layout.languagePolish'
  | 'layout.admin'
  | 'layout.signOut'
  | 'layout.terms'
  | 'layout.privacy'
  | 'layout.contact'
  | 'home.badge'
  | 'home.titleLine1'
  | 'home.titleLine2'
  | 'home.description'
  | 'home.start'
  | 'home.companyLabel'
  | 'home.companyPlaceholder'
  | 'home.submit'
  | 'home.noticeSessionTitle'
  | 'home.noticePermissionTitle'
  | 'home.noticeCompanyRequiredTitle'
  | 'home.sessionExpiredMessage'
  | 'home.permissionDeniedMessage'
  | 'home.companyRequiredMessage'
  | 'auth.welcome'
  | 'auth.authenticatingFor'
  | 'auth.close'
  | 'auth.secureEmailLogin'
  | 'auth.emailPlaceholder'
  | 'auth.requestMagicLink'
  | 'auth.magicLinkSent'
  | 'auth.termsRequired'
  | 'auth.termsConsent'
  | 'auth.technicalAccountHint'
  | 'auth.gdprNoticeTitle'
  | 'auth.gdprNoticeBody'
  | 'legal.termsTitle'
  | 'legal.privacyTitle'
  | 'legal.version'
  | 'legal.lastUpdated'
  | 'legal.termsIntro'
  | 'legal.privacyIntro'
  | 'legal.contactTitle'
  | 'legal.contactBody'
  | 'cookies.title'
  | 'cookies.body'
  | 'cookies.accept'
  | 'cookies.policyLink'
  | 'results.aiNoticeTitle'
  | 'results.aiNoticeBody'
  | 'assessment.phaseFoundations'
  | 'assessment.phaseAudit'
  | 'assessment.phaseReadiness'
  | 'assessment.loadingSaving'
  | 'assessment.loadingAnalysis'
  | 'assessment.loadingRecommendations'
  | 'assessment.loadingValidation'
  | 'assessment.loadingBusinessCases'
  | 'assessment.back'
  | 'assessment.seeResults'
  | 'assessment.skip'
  | 'assessment.skipHint'
  | 'results.loadingPreparing'
  | 'results.loadingTranslation'
  | 'results.loadingRecommendations'
  | 'results.loadingBusinessCases'
  | 'results.tooltipScore'
  | 'results.pathAudit'
  | 'results.pathReadiness'
  | 'results.reportTitle'
  | 'results.newAssessment'
  | 'results.globalScore'
  | 'results.snapshotEyebrow'
  | 'results.snapshotTitle'
  | 'results.foundationTitle'
  | 'results.foundationSubtitle'
  | 'results.barriersTitle'
  | 'results.barriersSubtitle'
  | 'results.opportunitiesTitle'
  | 'results.opportunitiesSubtitle'
  | 'results.doNothingTitle'
  | 'results.doNothingSubtitle'
  | 'results.helpTitle'
  | 'results.businessCasesSubtitle'
  | 'results.executiveCases'
  | 'results.businessCaseLabel'
  | 'results.businessCaseDescriptionFallback'
  | 'results.offersPreparing'
  | 'results.sectionPreparing'
  | 'results.benchmark'
  | 'results.contactTitle'
  | 'results.contactPitch'
  | 'results.contactEditableHint'
  | 'results.contactCompany'
  | 'results.contactEmail'
  | 'results.contactPhone'
  | 'results.contactMessage'
  | 'results.contactPhonePlaceholder'
  | 'results.contactMessagePlaceholder'
  | 'results.contactSending'
  | 'results.contactSuccess'
  | 'results.contactSubmit'
  | 'results.contactSubject'
  | 'results.backToAdmin'
  | 'admin.loadingUnauthorized'
  | 'admin.loadingSyncError'
  | 'admin.badge'
  | 'admin.title'
  | 'admin.titleAccent'
  | 'admin.subtitle'
  | 'admin.totalRecords'
  | 'admin.systemLive'
  | 'admin.empty'
  | 'admin.score'
  | 'admin.ref'
  | 'admin.verify'
  | 'admin.unregisteredEntity'
  | 'admin.backToAssessment'
  | 'admin.backToResults'
  | 'adminDetails.backToDashboard'
  | 'adminDetails.backToAssessment'
  | 'adminDetails.backToResults'
  | 'adminDetails.executiveReport'
  | 'adminDetails.organization'
  | 'adminDetails.maturityScore'
  | 'adminDetails.keyObservation'
  | 'adminDetails.viewClientResults'
  | 'adminDetails.analyzing'
  | 'adminDetails.generateAudit'
  | 'adminDetails.backToList'
  | 'adminDetails.strategyOffering'
  | 'adminDetails.sectionPreparing'
  | 'adminDetails.aiError';

type TranslationDictionary = Record<TranslationKey, string>;

const en: TranslationDictionary = {
  'layout.theme': 'Theme',
  'layout.themeLight': 'Light',
  'layout.themeDark': 'Dark',
  'layout.language': 'Language',
  'layout.languageEnglish': 'EN',
  'layout.languagePolish': 'PL',
  'layout.admin': 'System Admin',
  'layout.signOut': 'Sign Out',
  'layout.terms': 'Terms',
  'layout.privacy': 'Privacy Policy',
  'layout.contact': 'Contact: info@seargin.com',
  'home.badge': 'Future-Ready Analysis',
  'home.titleLine1': 'Data Maturity',
  'home.titleLine2': '& AI Readiness',
  'home.description': 'Evaluate your Data & AI capabilities and benchmark your results against market standards.',
  'home.start': 'Initialize Scan',
  'home.companyLabel': "Organization's name",
  'home.companyPlaceholder': 'Enter company name...',
  'home.submit': 'Get started',
  'home.noticeSessionTitle': 'Session Notice',
  'home.noticePermissionTitle': 'Permission Denied',
  'home.noticeCompanyRequiredTitle': 'Company Name Required',
  'home.sessionExpiredMessage': 'Your session expired. Please log in again to continue.',
  'home.permissionDeniedMessage': 'You do not have admin permissions to access this area.',
  'home.companyRequiredMessage': 'Company name is required to continue. Please provide it again.',
  'auth.welcome': 'Welcome',
  'auth.authenticatingFor': 'Authenticating for',
  'auth.close': 'Close',
  'auth.secureEmailLogin': 'Secure email login',
  'auth.emailPlaceholder': 'name@company.com',
  'auth.requestMagicLink': 'Request Magic Link',
  'auth.magicLinkSent': 'Magic Link sent! Check your inbox.',
  'auth.termsRequired': 'Accept the Terms and Privacy Policy to continue.',
  'auth.termsConsent': 'I have read and accept the Terms and Privacy Policy.',
  'auth.technicalAccountHint': 'The account is technical and temporary. It is used for authorization, session security, limits, and report generation.',
  'auth.gdprNoticeTitle': 'Information clause (Art. 13 GDPR)',
  'auth.gdprNoticeBody': 'Controller: Seargin sp. z o.o., ul. Chrzanowskiego 11, 80-278 Gdańsk. Purpose: providing access to the application, security, and claims. Legal bases: Art. 6(1)(b) and Art. 6(1)(f) GDPR. You have the right to access, rectify, erase, restrict, and port your data and to object to processing. Full information — including international transfers and contact to the Data Protection Officer — is in the Privacy Policy.',
  'legal.termsTitle': 'Terms of Data Maturity & AI Readiness',
  'legal.privacyTitle': 'Privacy Policy of Data Maturity & AI Readiness',
  'legal.version': 'Version 1.0',
  'legal.lastUpdated': 'Effective date to be completed before publication.',
  'legal.termsIntro': 'This page is a publication placeholder for the full Terms. Use the approved legal document text here before launch.',
  'legal.privacyIntro': 'This page is a publication placeholder for the full Privacy Policy. Use the approved legal document text here before launch.',
  'legal.contactTitle': 'Contact',
  'legal.contactBody': 'For matters related to the application or privacy, contact us at info@seargin.com.',
  'cookies.title': 'Essential cookies',
  'cookies.body': 'This application uses only essential cookies needed to log you in and keep you securely signed in. They cannot be turned off, so there is nothing to configure — we do not use analytics or marketing cookies.',
  'cookies.accept': 'OK',
  'cookies.policyLink': 'Read the Privacy Policy',
  'results.aiNoticeTitle': 'AI-generated report',
  'results.aiNoticeBody': 'This Report was produced with the support of artificial intelligence. The Assessment and Benchmark are computed by static rules; recommendations and examples are generated by AI (currently OpenAI gpt-4.1-mini) and may contain errors. The Report does not constitute an automated decision producing legal effects. If any item raises doubts or objections, contact info@seargin.com.',
  'assessment.phaseFoundations': 'Phase 1: Foundations',
  'assessment.phaseAudit': 'Phase 2: Strategic Audit',
  'assessment.phaseReadiness': 'Phase 2: Readiness Assessment',
  'assessment.loadingSaving': 'Saving your results...',
  'assessment.loadingAnalysis': 'Analyzing your maturity profile...',
  'assessment.loadingRecommendations': 'Generating strategic recommendations...',
  'assessment.loadingValidation': 'Validating report quality...',
  'assessment.loadingBusinessCases': 'Building business cases...',
  'assessment.back': 'Back',
  'assessment.seeResults': 'See Results',
  'assessment.skip': 'Skip',
  'assessment.skipHint': "If you skip, you'll get 0 points for this specific question",
  'results.loadingPreparing': 'Preparing your dashboard...',
  'results.loadingTranslation': 'Preparing translation...',
  'results.loadingRecommendations': 'Generating strategic recommendations...',
  'results.loadingBusinessCases': 'Finalizing business cases...',
  'results.tooltipScore': 'Score',
  'results.pathAudit': 'AI Strategic Audit',
  'results.pathReadiness': 'AI Readiness Assessment',
  'results.reportTitle': 'Maturity Report',
  'results.newAssessment': 'New Assessment',
  'results.globalScore': 'Global Score',
  'results.snapshotEyebrow': 'A quick look at what your business is good at and what needs work',
  'results.snapshotTitle': 'Where You Stand Today',
  'results.foundationTitle': 'Your Business Foundation',
  'results.foundationSubtitle': 'Checking if your data is reliable and your systems work together',
  'results.barriersTitle': 'What Is Stopping You',
  'results.barriersSubtitle': 'Serious risks costing you money and blocking growth',
  'results.opportunitiesTitle': 'What you can win',
  'results.opportunitiesSubtitle': 'Opportunities for your business once you fix the main problems',
  'results.doNothingTitle': 'If You Do Nothing',
  'results.doNothingSubtitle': 'Likely Outcome',
  'results.helpTitle': 'Our Offer',
  'results.businessCasesSubtitle': 'Business-case view: scope, expected timeline, cost level, and expected client value.',
  'results.executiveCases': 'Executive Cases',
  'results.businessCaseLabel': 'Business Case',
  'results.businessCaseDescriptionFallback': 'Project scope is being prepared for this case.',
  'results.offersPreparing': 'Offers are still preparing for this language. Please wait a moment.',
  'results.sectionPreparing': 'This section is still preparing. Please wait.',
  'results.benchmark': 'Benchmark',
  'results.contactTitle': 'Contact Us',
  'results.contactPitch': "Most teams at this stage lose months trying to patch these gaps alone. If you want a sharper first 30-day plan, send us a note and we'll show you where to start.",
  'results.contactEditableHint': 'We prefilled the details from this assessment. Change anything if you used placeholder data.',
  'results.contactCompany': 'Company name',
  'results.contactEmail': 'Work email',
  'results.contactPhone': 'Phone number (optional)',
  'results.contactMessage': 'What do you want help with?',
  'results.contactPhonePlaceholder': '+48 123 456 789',
  'results.contactMessagePlaceholder': 'Tell us what you want to improve first, what is blocking you, or whether you want a short strategy call.',
  'results.contactSending': 'Sending...',
  'results.contactSuccess': 'Request sent successfully. We will contact you soon.',
  'results.contactSubmit': 'Send Request',
  'results.contactSubject': 'Request for AI readiness follow-up',
  'results.backToAdmin': 'Back to Admin Panel',
  'admin.loadingUnauthorized': 'Unauthorized access',
  'admin.loadingSyncError': 'Failed to sync with secure database.',
  'admin.badge': 'Administrative Oversight',
  'admin.title': 'Audit',
  'admin.titleAccent': 'Inventory',
  'admin.subtitle': 'Validate organizational AI readiness and maintain professional oversight of strategic assessment records.',
  'admin.totalRecords': 'Total Records',
  'admin.systemLive': 'System Live',
  'admin.empty': 'Audit database is currently empty',
  'admin.score': 'Score',
  'admin.ref': 'Ref',
  'admin.verify': 'Verify Report',
  'admin.unregisteredEntity': 'Unregistered Entity',
  'admin.backToAssessment': 'Back to Assessment',
  'admin.backToResults': 'Back to Results',
  'adminDetails.backToDashboard': 'Back to List',
  'adminDetails.backToAssessment': 'Back to Assessment',
  'adminDetails.backToResults': 'Back to Results',
  'adminDetails.executiveReport': 'Executive Report',
  'adminDetails.organization': 'Organization',
  'adminDetails.maturityScore': 'Maturity Score',
  'adminDetails.keyObservation': 'Key Observation',
  'adminDetails.viewClientResults': 'View Client Results',
  'adminDetails.analyzing': 'Analyzing...',
  'adminDetails.generateAudit': 'Generate Audit',
  'adminDetails.backToList': 'Back to List',
  'adminDetails.strategyOffering': 'Strategic Audit & Offering',
  'adminDetails.sectionPreparing': 'This section is still preparing. Please wait.',
  'adminDetails.aiError': 'AI ERROR:',
};

const pl: TranslationDictionary = {
  'layout.theme': 'Motyw',
  'layout.themeLight': 'Jasny',
  'layout.themeDark': 'Ciemny',
  'layout.language': 'Język',
  'layout.languageEnglish': 'EN',
  'layout.languagePolish': 'PL',
  'layout.admin': 'Panel Admina',
  'layout.signOut': 'Wyloguj',
  'layout.terms': 'Regulamin',
  'layout.privacy': 'Polityka prywatności',
  'layout.contact': 'Kontakt: info@seargin.com',
  'home.badge': 'Analiza Gotowości',
  'home.titleLine1': 'Data Maturity',
  'home.titleLine2': '& AI Readiness',
  'home.description': 'Oceń swoje możliwości w obszarze danych i AI oraz porównaj wynik ze standardami rynkowymi.',
  'home.start': 'Rozpocznij Analizę',
  'home.companyLabel': 'Nazwa organizacji',
  'home.companyPlaceholder': 'Wpisz nazwę firmy...',
  'home.submit': 'Rozpocznij',
  'home.noticeSessionTitle': 'Informacja o Sesji',
  'home.noticePermissionTitle': 'Brak Uprawnień',
  'home.noticeCompanyRequiredTitle': 'Wymagana Nazwa Firmy',
  'home.sessionExpiredMessage': 'Twoja sesja wygasła. Zaloguj się ponownie, aby kontynuować.',
  'home.permissionDeniedMessage': 'Nie masz uprawnień administratora, aby wejść do tej sekcji.',
  'home.companyRequiredMessage': 'Aby kontynuować, podaj nazwę firmy ponownie.',
  'auth.welcome': 'Witaj',
  'auth.authenticatingFor': 'Logowanie dla',
  'auth.close': 'Zamknij',
  'auth.secureEmailLogin': 'Bezpieczne logowanie e-mailem',
  'auth.emailPlaceholder': 'nazwa@firma.com',
  'auth.requestMagicLink': 'Wyślij Magic Link',
  'auth.magicLinkSent': 'Magic Link został wysłany. Sprawdź skrzynkę.',
  'auth.termsRequired': 'Aby kontynuować, zaakceptuj Regulamin i Politykę prywatności.',
  'auth.termsConsent': 'Zapoznałem/am się z Regulaminem i Polityką prywatności oraz akceptuję ich treść.',
  'auth.technicalAccountHint': 'Konto ma charakter techniczny i tymczasowy. Służy autoryzacji, bezpieczeństwu sesji, limitom i wygenerowaniu raportu.',
  'auth.gdprNoticeTitle': 'Klauzula informacyjna (art. 13 RODO)',
  'auth.gdprNoticeBody': 'Administrator: Seargin sp. z o.o., ul. Chrzanowskiego 11, 80-278 Gdańsk. Cel: świadczenie dostępu do aplikacji, bezpieczeństwo oraz dochodzenie roszczeń. Podstawy prawne: art. 6 ust. 1 lit. b i f RODO. Przysługuje Ci prawo dostępu, sprostowania, usunięcia, ograniczenia, przenoszenia danych oraz sprzeciwu wobec przetwarzania. Pełna informacja, w tym o transferach poza EOG i kontakcie do inspektora ochrony danych, znajduje się w Polityce prywatności.',
  'legal.termsTitle': 'Regulamin aplikacji Data Maturity & AI Readiness',
  'legal.privacyTitle': 'Polityka prywatności aplikacji Data Maturity & AI Readiness',
  'legal.version': 'Wersja 1.0',
  'legal.lastUpdated': 'Data obowiązywania do uzupełnienia przed publikacją.',
  'legal.termsIntro': 'To miejsce publikacji pełnego Regulaminu. Przed wdrożeniem należy wkleić zatwierdzoną treść dokumentu prawnego.',
  'legal.privacyIntro': 'To miejsce publikacji pełnej Polityki prywatności. Przed wdrożeniem należy wkleić zatwierdzoną treść dokumentu prawnego.',
  'legal.contactTitle': 'Kontakt',
  'legal.contactBody': 'W sprawach związanych z aplikacją lub prywatnością skontaktuj się z nami pod adresem info@seargin.com.',
  'cookies.title': 'Niezbędne pliki cookies',
  'cookies.body': 'Aplikacja używa wyłącznie niezbędnych plików cookies, potrzebnych do zalogowania i utrzymania bezpiecznej sesji. Nie można ich wyłączyć, więc nie ma tu nic do konfigurowania — nie stosujemy cookies analitycznych ani marketingowych.',
  'cookies.accept': 'OK',
  'cookies.policyLink': 'Przeczytaj Politykę prywatności',
  'results.aiNoticeTitle': 'Raport wygenerowany przy wsparciu AI',
  'results.aiNoticeBody': 'Niniejszy Raport został przygotowany przy wsparciu sztucznej inteligencji. Ocena oraz Benchmark generowane są w oparciu o reguły statyczne; rekomendacje oraz przykłady praktyczne tworzone są przez AI (obecnie OpenAI gpt-4.1-mini) i mogą zawierać błędy. Raport nie stanowi zautomatyzowanej decyzji wywołującej skutki prawne. Jeśli któraś pozycja Raportu budzi wątpliwości lub sprzeciw, skontaktuj się z info@seargin.com.',
  'assessment.phaseFoundations': 'Etap 1: Fundamenty',
  'assessment.phaseAudit': 'Etap 2: Audyt Strategiczny',
  'assessment.phaseReadiness': 'Etap 2: Ocena Gotowości',
  'assessment.loadingSaving': 'Zapisywanie wyników...',
  'assessment.loadingAnalysis': 'Analizowanie profilu dojrzałości...',
  'assessment.loadingRecommendations': 'Generowanie rekomendacji strategicznych...',
  'assessment.loadingValidation': 'Weryfikowanie jakości raportu...',
  'assessment.loadingBusinessCases': 'Budowanie business case\'ow...',
  'assessment.back': 'Wstecz',
  'assessment.seeResults': 'Zobacz Wyniki',
  'assessment.skip': 'Pomiń',
  'assessment.skipHint': 'Jeśli pominiesz to pytanie, otrzymasz za nie 0 punktów',
  'results.loadingPreparing': 'Przygotowywanie dashboardu...',
  'results.loadingTranslation': 'Przygotowujemy tłumaczenie...',
  'results.loadingRecommendations': 'Generowanie rekomendacji strategicznych...',
  'results.loadingBusinessCases': 'Finalizowanie business case\'ow...',
  'results.tooltipScore': 'Wynik',
  'results.pathAudit': 'Strategiczny Audyt AI',
  'results.pathReadiness': 'Ocena Gotowości AI',
  'results.reportTitle': 'Raport Dojrzałości',
  'results.newAssessment': 'Nowa Analiza',
  'results.globalScore': 'Wynik Globalny',
  'results.snapshotEyebrow': 'Szybki przegląd tego, co działa dobrze, a co wymaga poprawy',
  'results.snapshotTitle': 'Gdzie Jesteś Dzisiaj',
  'results.foundationTitle': 'Fundament Biznesu',
  'results.foundationSubtitle': 'Sprawdzamy, czy Twoje dane są wiarygodne i czy systemy dobrze ze sobą współpracują',
  'results.barriersTitle': 'Co Cię Blokuje',
  'results.barriersSubtitle': 'Poważne ryzyka, które kosztują pieniądze i blokują wzrost',
  'results.opportunitiesTitle': 'Co możesz zyskać',
  'results.opportunitiesSubtitle': 'Szanse dla firmy po usunięciu najważniejszych problemów',
  'results.doNothingTitle': 'Jeśli Nic Nie Zmienisz',
  'results.doNothingSubtitle': 'Co będzie dalej',
  'results.helpTitle': 'Nasze Oferty',
  'results.businessCasesSubtitle': 'Widok business case: zakres, przewidywany czas, poziom kosztu i oczekiwana wartość biznesowa.',
  'results.executiveCases': 'Business Case\'y Zarządcze',
  'results.businessCaseLabel': 'Business Case',
  'results.businessCaseDescriptionFallback': 'Zakres projektu dla tego case\'a jest w przygotowaniu.',
  'results.offersPreparing': 'Oferty dla tego języka są jeszcze przygotowywane. Poczekaj chwilę.',
  'results.sectionPreparing': 'Ta sekcja jest jeszcze przygotowywana. Poczekaj chwilę.',
  'results.benchmark': 'Benchmark',
  'results.contactTitle': 'Skontaktuj Się z Nami',
  'results.contactPitch': 'Na tym etapie większość zespołów traci miesiące, próbując samodzielnie łatać te luki. Jeśli chcesz dostać konkretny plan na pierwsze 30 dni, napisz do nas i pokażemy Ci, od czego zacząć.',
  'results.contactEditableHint': 'Uzupełniliśmy dane z tego testu. Możesz je zmienić, jeśli wcześniej wpisałeś testowe informacje.',
  'results.contactCompany': 'Nazwa firmy',
  'results.contactEmail': 'Adres e-mail',
  'results.contactPhone': 'Telefon (opcjonalnie)',
  'results.contactMessage': 'W czym możemy pomóc?',
  'results.contactPhonePlaceholder': '+48 123 456 789',
  'results.contactMessagePlaceholder': 'Napisz, co chcesz poprawić najpierw, co Cię blokuje albo czy chcesz krótką rozmowę strategiczną.',
  'results.contactSending': 'Wysyłanie...',
  'results.contactSuccess': 'Zapytanie zostało wysłane pomyślnie. Skontaktujemy się wkrótce.',
  'results.contactSubmit': 'Wyślij Zapytanie',
  'results.contactSubject': 'Prośba o kontakt po ocenie gotowości AI',
  'results.backToAdmin': 'Powrót do Panelu Admina',
  'admin.loadingUnauthorized': 'Brak autoryzacji',
  'admin.loadingSyncError': 'Nie udało się zsynchronizować z bezpieczną bazą danych.',
  'admin.badge': 'Nadzór Administracyjny',
  'admin.title': 'Rejestr',
  'admin.titleAccent': 'Audytów',
  'admin.subtitle': 'Weryfikuj gotowość organizacji do AI i nadzoruj strategiczne rekordy ocen.',
  'admin.totalRecords': 'Liczba Audytów',
  'admin.systemLive': 'System Online',
  'admin.empty': 'Baza audytów jest obecnie pusta',
  'admin.score': 'Wynik',
  'admin.ref': 'Ref',
  'admin.verify': 'Zweryfikuj Raport',
  'admin.unregisteredEntity': 'Nieznana Organizacja',
  'admin.backToAssessment': 'Powrót do Ankiety',
  'admin.backToResults': 'Powrót do Wyników',
  'adminDetails.backToDashboard': 'Powrót do Listy',
  'adminDetails.backToAssessment': 'Powrót do Ankiety',
  'adminDetails.backToResults': 'Powrót do Wyników',
  'adminDetails.executiveReport': 'Raport Zarządczy',
  'adminDetails.organization': 'Organizacja',
  'adminDetails.maturityScore': 'Wynik Dojrzałości',
  'adminDetails.keyObservation': 'Kluczowa Obserwacja',
  'adminDetails.viewClientResults': 'Zobacz Wyniki Klienta',
  'adminDetails.analyzing': 'Analiza...',
  'adminDetails.generateAudit': 'Wygeneruj Audyt',
  'adminDetails.backToList': 'Powrót do Listy',
  'adminDetails.strategyOffering': 'Audyt Strategiczny i Oferta',
  'adminDetails.sectionPreparing': 'Ta sekcja jest jeszcze przygotowywana. Poczekaj chwilę.',
  'adminDetails.aiError': 'BŁĄD AI:',
};

export const translations: Record<Language, TranslationDictionary> = { en, pl };

